<template>
  <div ref="container" class="page-drawer">
    <cv-button kind="ghost" @click="handleCollapseDrawer">
      <svg-icon v-if="isExpanded" name="ArrowDown24" kind="action" />
      <svg-icon v-else name="ArrowUp24" kind="action" />
    </cv-button>
    <div class="page-drawer-content">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { useVm } from "@utils/vm";

const { vm, vmExpose } = useVm();

const props = withDefaults(
  defineProps<{
    name: string;
    expanded?: boolean;
  }>(),
  { expanded: false },
);

const emit = defineEmits<{
  (e: "collapseDrawer"): void;
  (e: "drawerReady"): void;
}>();

const container = ref<HTMLDivElement>();
const isExpanded = ref<boolean>(props.expanded);

function handleCollapseDrawer(): void {
  emit("collapseDrawer");
}

function handleToggle(): void {
  isExpanded.value = !isExpanded.value;
}

vmExpose({
  isExpanded,
  handleToggle,
});

onMounted(() => {
  vm.$pageDrawer.register(vm);
  emit("drawerReady");
});
</script>

<style lang="scss">
@import "@styles/abstracts/variables";
@import "@styles/abstracts/mixins";
@import "carbon-components/scss/globals/scss/motion.scss";
@import "@styles/themes/sls-theme/tokens/typo";
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/layout/z-map";
.page-drawer {
  // If the page drawer transition duration is changed please update handleCollapseDrawer in page.drawer.mixin.js
  // as the function need to know when is the animation end
  transition: all $duration--moderate-02 ease-in-out;

  @include media("tablet-max") {
    .page-drawer-content {
      overflow-x: auto;
      margin-right: $spacing-05;
      // Margin bottom for space allowance of WOGGA to not overlap with the card actions
      margin-bottom: $spacing-07;

      .drawer-side-expand & {
        margin-right: 0;
      }
    }
  }

  @include media("mobile-landscape", "desktop") {
    .page-drawer-content {
      max-width: 100%;
      margin-right: 0;
      overflow: unset;
    }

    .bx--btn.bx--tooltip__trigger {
      display: none;
    }
  }

  &:has(.empty-placeholder) {
    .button-group {
      @include media("tablet") {
        margin-top: 0;
      }
    }
  }

  > .bx--btn {
    display: flex;
    width: 100%;
    max-width: 100%;
    justify-content: center;
    padding-top: $spacing-03;
    padding-bottom: $spacing-01;
    min-height: auto;
    color: $interactive-01;

    @include media("mobile-landscape", "desktop") {
      display: none;
    }

    @include media("tablet-max") {
      .drawer-side-expand & {
        display: none;
      }
    }

    &:active {
      background-color: inherit;
    }
    &:focus {
      box-shadow: none;
    }
  }

  .button-group {
    position: relative;
    display: none;
    gap: $spacing-05;
    align-items: center;
    justify-content: flex-end;
    margin-top: -$spacing-09;
    z-index: calc(#{z-map("default")} + 4);

    @include media("tablet-min") {
      display: flex;
    }

    @include media("desktop") {
      margin: $spacing-05 $spacing-05 $spacing-05 0;
      z-index: z-map("default");
    }
  }

  .accordion-toggle {
    position: sticky;
    left: 1rem;
    display: flex;
    gap: $spacing-05;
    max-width: 50%;
    margin: $spacing-05;
    background-color: $ui-03;
    z-index: calc(#{z-map("default")} + 9);

    .bx--link {
      &:first-child {
        position: relative;
        &::after {
          position: absolute;
          content: "";
          height: 100%;
          width: 1px;
          right: -0.5rem;
          background-color: $ui-04;
        }
      }
      &[aria-disabled="true"] {
        color: $disabled-02;
        opacity: 1;
      }
    }

    @include media("mobile-landscape", "desktop") {
      top: 0;
      left: unset;
      justify-content: flex-end;
      max-width: unset;
      margin: 0 $spacing-05 $spacing-05 $spacing-05;
      padding: $spacing-05 $spacing-05 $spacing-05 0;
      border-bottom: 1px solid $ui-04;
      z-index: calc(#{z-map("default")} + 3);
    }
  }
  .bx--accordion {
    padding: $spacing-02 $spacing-05;
    @include media("tablet-max") {
      width: min-content;
      padding: 0 $spacing-03;
      .bx--accordion__item {
        margin-bottom: 0;
      }
    }

    @include media("mobile-landscape") {
      width: 100%;
      padding: $spacing-02 $spacing-05;
      .bx--accordion__item {
        &.bx--accordion__item--active {
          > .bx--accordion__heading {
            .bx--accordion__title {
              &::after {
                left: unset;
                top: 0.75rem;
                transform: rotate(225deg);
              }
            }
          }
        }
      }

      .bx--accordion__item {
        display: block;
        .bx--accordion__heading {
          min-height: 3.2rem;
          width: 100%;
          border-radius: 10px 10px 0 0;
          .bx--accordion__title {
            &::after {
              right: 1rem;
              top: 0.5rem;
              transform: rotate(45deg);
            }
          }
        }

        .bx--accordion__content {
          display: block;
        }
      }
    }
  }

  .bx--accordion__item {
    .bx--accordion__heading {
      width: 100%;
      .bx--accordion__title {
        display: flex;
        align-items: center;
        margin: 0;
        padding: 0 $spacing-08 0 $spacing-05;

        .title {
          @include carbon--type-style("productive-heading-01", true);

          @include media("tablet-max") {
            @include carbon--type-style("card-label-01");
          }

          &:not(:has(.label)) {
            @include line-clamp(4);
            @include media("mobile-landscape", "desktop") {
              @include line-clamp(1);
            }
          }

          span:not(.label) {
            font-weight: 400;
          }
        }
      }
    }

    .bx--accordion__content {
      @include media("tablet-max") {
        display: flex;
        .annotation-list-item {
          margin: 0 $spacing-03 0 0;
          min-width: calc($page-side-width - $spacing-07);
        }
      }

      @include media("mobile-landscape", "desktop") {
        padding: $spacing-03;
        .annotation-list-item {
          max-width: calc($page-side-width - $spacing-07);
          min-width: 100%;
          width: 100%;
          margin: 0 0 $spacing-03 0;
        }
      }
    }

    .bx--skeleton__text {
      margin-bottom: 0;
    }

    &.loading {
      .bx--accordion__title {
        &::after {
          opacity: 0;
        }
      }
    }

    @include media("tablet-max") {
      display: flex;
      &:not(.bx--accordion__item--active) {
        .bx--accordion__content {
          display: none;
        }
      }

      &.bx--accordion__item--active {
        > .bx--accordion__heading {
          .bx--accordion__title {
            &::after {
              top: 0.5rem;
              left: 50%;
              transform: rotate(135deg) translateX(50%);
            }
          }
        }
      }

      .bx--accordion__heading {
        min-height: 15rem;
        width: 3.2rem;
        border-radius: 10px 0 0 10px;
        .bx--accordion__title {
          display: flex;
          align-items: center;
          height: 100%;
          margin: 0;
          padding: $spacing-03;
          text-align: center;
          word-break: break-word;
          &::after {
            top: 1rem;
            right: 50%;
            transform: rotate(315deg) translateX(50%);
          }
        }
      }
    }
  }
}
</style>
